import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, ChevronDown, ChevronUp } from 'lucide-react';

interface CookieSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const CookieSettings: React.FC<CookieSettingsProps> = ({ isOpen, onClose }) => {
  const [preferences, setPreferences] = useState({
    essential: true, // Always enabled
    functional: true,
    analytics: true,
    advertising: false,
    social: false
  });

  useEffect(() => {
    // Load saved preferences
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentGiven', 'true');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Cookie className="h-5 w-5 text-ta-green mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Cookie Settings</h3>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="space-y-4">
            {/* Essential Cookies */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium text-gray-900">Essential Cookies</h4>
                  <p className="text-sm text-gray-600">Required for basic website functionality</p>
                </div>
                <div className="relative">
                  <input 
                    type="checkbox" 
                    checked={preferences.essential}
                    disabled
                    className="h-4 w-4 text-ta-green border-gray-300 rounded cursor-not-allowed"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">These cookies are necessary for the website to function and cannot be disabled.</p>
            </div>

            {/* Functional Cookies */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium text-gray-900">Functional Cookies</h4>
                  <p className="text-sm text-gray-600">Enhance website experience</p>
                </div>
                <div className="relative">
                  <input 
                    type="checkbox" 
                    checked={preferences.functional}
                    onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                    className="h-4 w-4 text-ta-green border-gray-300 rounded"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">These cookies enable personalized features and save your preferences.</p>
            </div>

            {/* Analytics Cookies */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium text-gray-900">Analytics Cookies</h4>
                  <p className="text-sm text-gray-600">Help us improve our website</p>
                </div>
                <div className="relative">
                  <input 
                    type="checkbox" 
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="h-4 w-4 text-ta-green border-gray-300 rounded"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">These cookies collect anonymous data about how visitors use our website.</p>
            </div>

            {/* Advertising Cookies */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium text-gray-900">Advertising Cookies</h4>
                  <p className="text-sm text-gray-600">Personalized advertisements</p>
                </div>
                <div className="relative">
                  <input 
                    type="checkbox" 
                    checked={preferences.advertising}
                    onChange={(e) => setPreferences({ ...preferences, advertising: e.target.checked })}
                    className="h-4 w-4 text-ta-green border-gray-300 rounded"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">These cookies are used to show you relevant advertisements across different websites.</p>
            </div>

            {/* Social Media Cookies */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-medium text-gray-900">Social Media Cookies</h4>
                  <p className="text-sm text-gray-600">Enable social sharing features</p>
                </div>
                <div className="relative">
                  <input 
                    type="checkbox" 
                    checked={preferences.social}
                    onChange={(e) => setPreferences({ ...preferences, social: e.target.checked })}
                    className="h-4 w-4 text-ta-green border-gray-300 rounded"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">These cookies enable social media integration and sharing capabilities.</p>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-ta-green rounded-md hover:bg-ta-green-dark"
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieSettings;